<script lang="ts">
import { computed, defineComponent, h, unref } from 'vue';

import { classNames, EventProp } from '@components/utils';

import {
  makeDisabledProps,
  makeFocusProps,
  makeIconProps,
  makeReadonlyProps,
  makeSizeProps,
  makeThemeProps,
  makeValidationProps,
  provideTheme,
  useFocus,
  useIcon,
  useInput,
  useRender,
  useSize,
  useValidation
} from '../../composable';
import { makeInputProps } from '../../composable';
import { CdsIcon } from '../CdsIcon';

import CdsInputMessages from './CdsInputMessages.vue';

import type { IInputSlot } from '../../composable';
import type { PropType } from 'vue';

export const componentName = 'CdsInput';
export default defineComponent({
  name: componentName,
  props: {
    ...makeInputProps(),
    ...makeIconProps(),
    ...makeValidationProps(),
    ...makeFocusProps(),
    ...makeDisabledProps(),
    ...makeReadonlyProps(),
    ...makeSizeProps(),
    ...makeThemeProps(),

    'onClick:prepend': Function as PropType<EventProp>,
    'onClick:append': Function as PropType<EventProp>
  },
  emits: {
    'update:modelValue': (value: any) => true,
    'click:control': (event: MouseEvent) => true,
    'mousedown:control': (event: MouseEvent) => true
  },
  setup(props, { slots, emit, expose }) {
    const { id } = useInput(props, slots);
    const { sizeClasses } = useSize(props, componentName);
    const { themeClasses } = provideTheme(props);
    const messagesId = computed(() => `${unref(id)}-messages`);

    const {
      errorMessages,
      isPure,
      isDirty,
      isDisabled,
      isReadonly,
      isValid,
      isValidating,
      validate,
      resetValidation,
      reset,
      validationClasses
    } = useValidation(props, componentName, id);
    const messages = computed(() =>
      props.errorMessages.length
        ? props.errorMessages
        : unref(errorMessages).length
        ? unref(errorMessages)
        : props.messages
    );
    const { hasAppend, hasPrepend, handleClickPrepend, handleClickAppend } = useIcon(props, slots, componentName);
    const { isFocused, focusClasses } = useFocus(props, componentName);
    const hasMessages = computed<boolean>(
      () => (!!unref(messages).length || !!slots.messages || !!props.messages?.length) && !props.hideMessages
    );
    const hasDefault = computed(() => !!slots.default);

    const slotProps = computed<IInputSlot>(() => ({
      id,
      isDirty,
      isDisabled,
      isReadonly,
      isPure,
      isValid,
      isValidating,
      reset,
      resetValidation,
      validate
    }));

    useRender(() =>
      h(
        'div',
        {
          class: classNames(
            'cds-input',
            unref(validationClasses),
            unref(focusClasses),
            unref(sizeClasses),
            unref(themeClasses)
          )
        },
        [
          unref(hasPrepend) &&
            h('div', { class: 'cds-input__prepend', onClick: handleClickPrepend }, [
              slots.prepend?.(),
              !!props.prependIcon && h(CdsIcon, { name: props.prependIcon })
            ]),
          unref(hasDefault) &&
            h(
              'div',
              {
                class: 'cds-input__control',
                onClick: (event: MouseEvent) => emit('click:control', event),
                onMousedown: (event: MouseEvent) => emit('mousedown:control', event)
              },
              slots.default?.(unref(slotProps))
            ),
          unref(hasAppend) &&
            h('div', { class: 'cds-input__append', onClick: handleClickAppend }, [
              slots.append?.(),
              !!props.appendIcon && h(CdsIcon, { name: props.appendIcon })
            ]),
          unref(hasMessages) &&
            h(
              CdsInputMessages,
              {
                id: unref(messagesId),
                active: unref(isFocused) || props.persistentMessages,
                error: !unref(isValid),
                messages: unref(messages)
              },
              {
                default: () => slots.messages?.()
              }
            )
        ]
      )
    );

    expose({
      id,
      isFocused,
      isDirty,
      isDisabled,
      isReadonly,
      isPure,
      isValid,
      isValidating,
      messages,
      reset,
      resetValidation,
      validate
    });

    return {};
  }
});
</script>

<style lang="scss">
@import './CdsInput';
</style>
